import { useMemo } from "react";
import { EagerLoaded } from "@common/types/base/orm";
import { Category } from "@common/types/category";
import { useScope } from "@state/scopeContext.lib";
import { useGlobalCategories, useWorkspaceCategories } from "@api/category.api";

export enum CategoryStructure {
    FLAT = "flat",
    TREE = "tree",
}

interface UseCategoriesOptions<T extends CategoryStructure> {
    structure: T;
    onlyStandardCategories?: boolean;
}

export function useCategories(
    options: UseCategoriesOptions<CategoryStructure.TREE>,
): Array<EagerLoaded<Category, "subcategories">>;
export function useCategories(
    options: UseCategoriesOptions<CategoryStructure.FLAT>,
): Category[];
export function useCategories<T extends CategoryStructure = CategoryStructure>({
    onlyStandardCategories = false,
    structure,
}: UseCategoriesOptions<T>):
    | Array<EagerLoaded<Category, "subcategories">>
    | Category[] {
    const categoriesToShow = useCategoriesToShow(onlyStandardCategories);

    const flat = useMemo(
        () =>
            categoriesToShow
                .map((category) => [
                    category,
                    ...(category.subcategories ?? []),
                ])
                .flat(),
        [categoriesToShow],
    );

    if (structure === CategoryStructure.TREE) {
        return categoriesToShow;
    }

    return flat;
}

function useCategoriesToShow(onlyStandardCategories: boolean) {
    const { workspaceId } = useScope();
    const workspaceCategories = useWorkspaceCategories();
    const globalCategories = useGlobalCategories();

    // When there is no active workspace (e.g. org-level settings pages),
    // fall back to the global-categories endpoint which returns standard
    // categories only — no workspaceId required.
    const hasWorkspace = Boolean(workspaceId);

    return useMemo(() => {
        if (!hasWorkspace) {
            return (globalCategories.data?.categories ?? []) as Array<
                EagerLoaded<Category, "subcategories">
            >;
        }

        if (onlyStandardCategories || workspaceCategories.isLoading) {
            return (workspaceCategories.data?.categories ?? []).map((c) => ({
                ...c,
                subcategories: c.subcategories?.filter(
                    (subcategory) => !subcategory.workspaceId,
                ),
            })) as Array<EagerLoaded<Category, "subcategories">>;
        }

        return (workspaceCategories.data?.categories ?? []) as Array<
            EagerLoaded<Category, "subcategories">
        >;
    }, [
        hasWorkspace,
        onlyStandardCategories,
        globalCategories.data?.categories,
        workspaceCategories.data?.categories,
        workspaceCategories.isLoading,
    ]);
}
